import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, where } from 'firebase/firestore';
import { 
  CheckSquare, 
  Square, 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Search, 
  Tag, 
  Clock, 
  AlertCircle, 
  CheckCircle,
  Filter,
  Save,
  Copy,
  ClipboardPaste
} from 'lucide-react';
import { useAdvancedPermissions } from '../hooks/useAdvancedPermissions';
import { useTheme } from '../contexts/ThemeContext';
import Button from './ui/Button';
import Alert from './ui/Alert';

const AdvancedTaskManager = ({ db, currentUser }) => {
  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newTask, setNewTask] = useState({ 
    name: '', 
    category: '',
    description: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editedCategoryName, setEditedCategoryName] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Utiliser le nouveau système de permissions avancées
  const { hasPermission, loading: permissionsLoading } = useAdvancedPermissions({ userId: currentUser?.id });
  const { theme } = useTheme();

  const canManageTasks = hasPermission({ resource: 'tasks', action: 'manage' });
  const canCreateTasks = hasPermission({ resource: 'tasks', action: 'create' });
  const canEditTasks = hasPermission({ resource: 'tasks', action: 'write' });
  const canDeleteTasks = hasPermission({ resource: 'tasks', action: 'delete' });
  const canViewTasks = hasPermission({ resource: 'tasks', action: 'read' });

  useEffect(() => {
    fetchCategories();
    fetchTasks();
  }, []);

  const fetchCategories = async () => {
    const q = query(collection(db, 'taskCategories'));
    const querySnapshot = await getDocs(q);
    const fetchedCategories = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setCategories(fetchedCategories);
  };

  const fetchTasks = async () => {
    const q = query(collection(db, 'tasks'));
    const querySnapshot = await getDocs(q);
    const fetchedTasks = querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data()
    }));
    setTasks(fetchedTasks);
  };

  const handleSaveTask = async () => {
    if (newTask.name && newTask.category) {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      try {
        const taskData = {
          name: newTask.name,
          category: newTask.category,
          description: newTask.description || '',
          createdAt: new Date(),
          createdBy: currentUser.userId,
          createdByName: `${currentUser.firstName} ${currentUser.lastName}`
        };

        if (isEditing && taskToEdit) {
          await updateDoc(doc(db, 'tasks', taskToEdit.id), taskData);
          setIsEditing(false);
          setTaskToEdit(null);
          setSuccess('Tâche modifiée avec succès');
        } else {
          await addDoc(collection(db, 'tasks'), taskData);
          setSuccess('Tâche créée avec succès');
        }
        
        setNewTask({ name: '', category: '', description: '' });
        setShowTaskForm(false);
        fetchTasks();
        
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error("Erreur lors de la sauvegarde de la tâche:", error);
        setError("Erreur lors de la sauvegarde de la tâche");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleEditTask = (task) => {
    if (!canEditTasks) return;
    setIsEditing(true);
    setTaskToEdit(task);
    setNewTask({
      name: task.name,
      category: task.category,
      description: task.description || ''
    });
    setShowTaskForm(true);
  };

  const handleDeleteTask = async (id) => {
    if (!canDeleteTasks) return;
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')) {
      await deleteDoc(doc(db, 'tasks', id));
      fetchTasks();
      setSuccess('Tâche supprimée avec succès');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleAddCategory = async () => {
    if (newCategory && canManageTasks) {
      await addDoc(collection(db, 'taskCategories'), { name: newCategory });
      setNewCategory('');
      fetchCategories();
      setSuccess('Catégorie ajoutée avec succès');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleUpdateCategory = async (id, updatedName) => {
    if (updatedName && canManageTasks) {
      await updateDoc(doc(db, 'taskCategories', id), { name: updatedName });
      fetchCategories();
      setSuccess('Catégorie modifiée avec succès');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!canManageTasks) return;
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      await deleteDoc(doc(db, 'taskCategories', id));
      fetchCategories();
      setSuccess('Catégorie supprimée avec succès');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleEditCategory = (category) => {
    if (!canManageTasks) return;
    setEditingCategory(category);
    setEditedCategoryName(category.name);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setEditedCategoryName('');
  };

  const handleSaveCategory = async () => {
    if (editingCategory && editedCategoryName && canManageTasks) {
      await updateDoc(doc(db, 'taskCategories', editingCategory.id), { name: editedCategoryName });
      setEditingCategory(null);
      setEditedCategoryName('');
      fetchCategories();
      setSuccess('Catégorie sauvegardée avec succès');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesCategory = filterCategory === 'all' || task.category === filterCategory;
    const matchesSearch = searchTerm === '' || 
      task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  if (!canViewTasks) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-400 dark:text-red-400" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Accès refusé</h2>
          <p className="text-gray-600 dark:text-gray-400">Vous n'avez pas les permissions nécessaires pour accéder au gestionnaire de tâches.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 dark:from-blue-500 dark:to-indigo-600 rounded-2xl flex items-center justify-center text-white">
              <CheckSquare className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Gestion Avancée des Tâches
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Gestion professionnelle des tâches avec système de permissions
              </p>
            </div>
          </div>

          {canCreateTasks && (
            <Button
              onClick={() => setShowTaskForm(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nouvelle Tâche
            </Button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert type="error" className="mb-6">
          {error}
        </Alert>
      )}

      {success && (
        <Alert type="success" className="mb-6">
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar with Filters */}
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Filtres</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Catégorie</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">Toutes catégories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recherche</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-400" size={16} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-10 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Categories Management */}
          {canManageTasks && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Gestion des Catégories</h3>
              <div className="flex space-x-2 mb-4">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Nouvelle catégorie"
                  className="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                <Button onClick={handleAddCategory} size="sm">
                  <Plus size={16} />
                </Button>
              </div>
              
              <div className="space-y-2">
                {categories.map((category) => (
                  <div key={category.id} className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg flex justify-between items-center bg-white dark:bg-gray-700">
                    {editingCategory && editingCategory.id === category.id ? (
                      <>
                        <input
                          type="text"
                          value={editedCategoryName}
                          onChange={(e) => setEditedCategoryName(e.target.value)}
                          className="flex-grow p-1 border border-gray-300 dark:border-gray-600 rounded mr-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <div className="flex space-x-1">
                          <Button onClick={handleSaveCategory} size="sm" variant="success">
                            <CheckCircle size={14} />
                          </Button>
                          <Button onClick={handleCancelEditCategory} size="sm" variant="secondary">
                            <X size={14} />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-gray-900 dark:text-white">{category.name}</span>
                        <div className="flex space-x-1">
                          <Button onClick={() => handleEditCategory(category)} size="sm" variant="warning">
                            <Edit size={14} />
                          </Button>
                          <Button onClick={() => handleDeleteCategory(category.id)} size="sm" variant="danger">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Liste des Tâches</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {filteredTasks.length} tâche{filteredTasks.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Task Form */}
          {showTaskForm && canCreateTasks && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6 border border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                {isEditing ? 'Modifier la tâche' : 'Nouvelle tâche'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nom de la tâche *</label>
                  <input
                    type="text"
                    value={newTask.name}
                    onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                    placeholder="Nom de la tâche"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Catégorie *</label>
                  <select
                    value={newTask.category}
                    onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Description de la tâche..."
                  rows="3"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  onClick={() => {
                    setShowTaskForm(false);
                    setIsEditing(false);
                    setTaskToEdit(null);
                    setNewTask({ name: '', category: '', description: '' });
                  }}
                  variant="secondary"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveTask}
                  isLoading={isSaving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Sauvegarder' : 'Créer'}
                </Button>
              </div>
            </div>
          )}

          {/* Tasks List */}
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <AlertCircle size={48} className="mx-auto mb-4 text-gray-400 dark:text-gray-400" />
              <p>Aucune tâche à afficher</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTasks.map((task) => {
                const category = categories.find(cat => cat.id === task.category);
                
                return (
                  <div
                    key={task.id}
                    className="p-6 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-grow">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          {task.name}
                        </h3>
                        {task.description && (
                          <p className="text-gray-600 dark:text-gray-300 mb-3">{task.description}</p>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          {category && (
                            <span className="flex items-center bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-3 py-1 rounded-full">
                              <Tag size={14} className="mr-1" />
                              {category.name}
                            </span>
                          )}
                          
                          {task.createdByName && (
                            <span className="flex items-center bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-3 py-1 rounded-full">
                              <Clock size={14} className="mr-1" />
                              Par {task.createdByName}
                            </span>
                          )}
                          
                          {task.createdAt && (
                            <span className="text-gray-500 dark:text-gray-400">
                              Créé le {new Date(task.createdAt.seconds * 1000).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>

                        {(canEditTasks || canDeleteTasks) && (
                        <div className="flex space-x-2 ml-4">
                          {canEditTasks && (
                            <Button
                              onClick={() => handleEditTask(task)}
                              variant="warning"
                              size="sm"
                            >
                              <Edit size={14} />
                            </Button>
                          )}
                          {canDeleteTasks && (
                            <Button
                              onClick={() => handleDeleteTask(task.id)}
                              variant="danger"
                              size="sm"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedTaskManager;
